import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaEdit, FaTrashAlt } from "react-icons/fa";
import { fetchAllTasks, fetchTasksByProject, fetchTasksByStoryId, fetchTaskStatuses, deleteTask } from "./taskService";
import { useResizableColumns } from "./issues/useResizableColumns";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import "../assets/css/Projects.css";
import "../assets/css/ViewCustomer.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";
import "../assets/css/DigixLeads.css";

const TASKS_ROWS_PER_PAGE_KEY = "tasksRowsPerPage";

function TasksTable({
  storyId,
  projectId,
  subProjectId,
  storyTitle,
  projectName,
  subProjectName,
  onRefresh,
  /** Optional heading above filters (e.g. View User Story page). */
  cardTitle,
  /** When false (e.g. PM tab hidden), skip task API fetch. */
  dataActive = true,
  projects = [],
  subprojects = [],
  loadingSubprojects = false,
  selectedProjectIdValue = "",
  selectedSubprojectIdValue = "",
  onProjectChange,
  onSubprojectChange,
  selectedStoryIdValue = "",
  onStoryChange,
  loadingUserStoriesForTasks = false,
  storiesForAll,
  allStoriesSelected = false,
  dashboardTaskStatusPreset = "",
}) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [taskPage, setTaskPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(TASKS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [titleSearch, setTitleSearch] = useState("");
  const [freeSearch, setFreeSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [taskStatuses, setTaskStatuses] = useState([]);
  const TASK_COL_DEFAULT_WIDTHS = [160, 200, 100, 120, 120, 120, 120, 120, 100];
  const { getColProps, ResizeHandle } = useResizableColumns(TASK_COL_DEFAULT_WIDTHS);

  const TASK_PRIORITIES = ["High", "Medium", "Low"];

  const taskStatusBucket = (statusRaw) => {
    const status = String(statusRaw || "").trim().toLowerCase();
    const compact = status.replace(/[\s_-]+/g, "");
    if (
      status.includes("to-do") ||
      status.includes("to do") ||
      status.includes("todo") ||
      compact.includes("todo")
    ) return "todo";
    if (
      status.includes("in progress") ||
      status.includes("in-progress") ||
      status.includes("in_progress") ||
      compact.includes("inprogress")
    ) return "inProgress";
    return "";
  };

  // Searchable dropdown state (Project / Subproject / User Story) above the tasks table
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [subprojectDropdownOpen, setSubprojectDropdownOpen] = useState(false);
  const [subprojectSearchQuery, setSubprojectSearchQuery] = useState("");
  const [storyDropdownOpen, setStoryDropdownOpen] = useState(false);
  const [storySearchQuery, setStorySearchQuery] = useState("");

  const projectDropdownRef = useRef(null);
  const subprojectDropdownRef = useRef(null);
  const storyDropdownRef = useRef(null);
  const lastFetchSignatureRef = useRef("");
  const activeFetchRequestRef = useRef(0);
  /** While true, navigation preset may sync status filters; cleared after user runs Search/Clear so filters stay manual. */
  const [dashboardPresetActive, setDashboardPresetActive] = useState(() => Boolean(dashboardTaskStatusPreset));

  useEffect(() => {
    const handleDocumentMouseDown = (e) => {
      const target = e?.target;

      if (projectDropdownOpen && projectDropdownRef.current && !projectDropdownRef.current.contains(target)) {
        setProjectDropdownOpen(false);
        setProjectSearchQuery("");
      }
      if (subprojectDropdownOpen && subprojectDropdownRef.current && !subprojectDropdownRef.current.contains(target)) {
        setSubprojectDropdownOpen(false);
        setSubprojectSearchQuery("");
      }
      if (storyDropdownOpen && storyDropdownRef.current && !storyDropdownRef.current.contains(target)) {
        setStoryDropdownOpen(false);
        setStorySearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [projectDropdownOpen, subprojectDropdownOpen, storyDropdownOpen]);

  // Stabilize dependency for aggregated mode.
  // Without this, the default empty array created on each render could trigger task re-fetching and cause UI blinking.
  const storiesForAllIdsKey = useMemo(() => {
    if (!Array.isArray(storiesForAll) || storiesForAll.length === 0) return "";
    const ids = storiesForAll
      .map((st) => st?.id ?? st?.User_Story_Id ?? st?.user_story_id ?? st?.UserStoryId)
      .filter((x) => x != null && x !== "");
    return ids.map((x) => String(x)).sort().join("|");
  }, [storiesForAll]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await fetchTaskStatuses();
        setTaskStatuses((Array.isArray(statuses) ? statuses : []).sort((a, b) => (a.Status_Id ?? a.status_id ?? 0) - (b.Status_Id ?? b.status_id ?? 0)));
      } catch (err) {
        console.error("Error fetching task statuses:", err);
      }
    };
    loadStatuses();
  }, []);

  useEffect(() => {
    if (!dashboardTaskStatusPreset) {
      setDashboardPresetActive(false);
      return;
    }
    setDashboardPresetActive(true);
  }, [dashboardTaskStatusPreset]);

  useEffect(() => {
    if (!dashboardTaskStatusPreset || !dashboardPresetActive) return;

    const matchedStatus = (taskStatuses || []).find((s) => {
      const value = s?.Status ?? s?.status ?? "";
      return taskStatusBucket(value) === dashboardTaskStatusPreset;
    });

    const fallbackStatus =
      dashboardTaskStatusPreset === "todo"
        ? "To Do"
        : dashboardTaskStatusPreset === "inProgress"
          ? "In Progress"
          : "";

    const statusValue = matchedStatus ? (matchedStatus.Status ?? matchedStatus.status ?? "") : fallbackStatus;
    if (!statusValue) return;

    setStatusFilter(statusValue);
    setTaskPage(1);
  }, [dashboardTaskStatusPreset, taskStatuses, dashboardPresetActive]);

  const loadTasks = async () => {
    if (!dataActive) {
      return;
    }
    const isGlobalAllScope =
      allStoriesSelected && !selectedProjectIdValue && !selectedSubprojectIdValue;
    const hasScopedProjectSelection = Boolean(selectedProjectIdValue || projectId);
    const fetchSignature = isGlobalAllScope
      ? "global-all"
      : `story:${storyId || ""}|project:${projectId || ""}|subproject:${subProjectId || ""}|allStories:${
          allStoriesSelected ? "1" : "0"
        }|storiesKey:${storiesForAllIdsKey}`;

    // In project-scoped "All User Stories" mode, fetch tasks directly by project/subproject.
    // This avoids waiting for stories index refresh and makes filter changes feel immediate.
    if (!storyId && allStoriesSelected && hasScopedProjectSelection) {
      const scopedProjectId = projectId ?? Number(selectedProjectIdValue);
      const scopedSubprojectId =
        subProjectId != null && subProjectId !== ""
          ? subProjectId
          : selectedSubprojectIdValue
            ? Number(selectedSubprojectIdValue)
            : null;
      const requestId = activeFetchRequestRef.current + 1;
      activeFetchRequestRef.current = requestId;
      setLoading(true);
      try {
        const data = await fetchTasksByProject(scopedProjectId, scopedSubprojectId);
        if (requestId !== activeFetchRequestRef.current) return;
        const arr = Array.isArray(data) ? data : [];
        setTasks(
          arr.map((t) => ({
            ...t,
            __projectId: scopedProjectId ? Number(scopedProjectId) : null,
            __subProjectId: scopedSubprojectId ? Number(scopedSubprojectId) : null,
          }))
        );
        return;
      } catch (err) {
        console.error("Error fetching project-scoped tasks:", err);
        if (requestId !== activeFetchRequestRef.current) return;
        setTasks([]);
        lastFetchSignatureRef.current = "";
        return;
      } finally {
        if (requestId !== activeFetchRequestRef.current) return;
        setLoading(false);
      }
    }

    // In non-global all-stories mode, stories index is required for aggregation.
    // While waiting, clear stale rows so old project tasks are not shown.
    if (!isGlobalAllScope && allStoriesSelected && loadingUserStoriesForTasks) {
      activeFetchRequestRef.current += 1;
      setTasks([]);
      setLoading(true);
      return;
    }

    // Prevent duplicate fetch/loading for identical scope (removes double blink).
    // Keep this check after "loading stories" guard so we don't cache a signature
    // before data is actually fetched, which can leave stale tasks on rapid filter changes.
    if (lastFetchSignatureRef.current === fetchSignature) {
      return;
    }
    lastFetchSignatureRef.current = fetchSignature;
    const requestId = activeFetchRequestRef.current + 1;
    activeFetchRequestRef.current = requestId;

    // When user selected "All User Stories", we need story scope first.
    // Avoid fetching tasks until the parent finishes loading stories.
    setLoading(true);
    try {
      // Single-story mode (existing behavior).
      if (storyId) {
        // When parent passes storyId but projectId isn't provided, derive it from `storiesForAll`.
        const selectedStory =
          (storiesForAll || []).find(
            (s) => String(s.id ?? s.User_Story_Id ?? s.user_story_id ?? s.UserStoryId) === String(storyId)
          ) || null;

        const derivedProjectId =
          projectId ??
          selectedStory?.projectId ??
          selectedStory?.Project_Id ??
          selectedStory?.project_id ??
          selectedStory?.ProjectId ??
          null;

        const derivedSubProjectId =
          subProjectId ??
          selectedStory?.subProjectId ??
          selectedStory?.Sub_Project_Id ??
          selectedStory?.sub_project_id ??
          selectedStory?.SubProjectId ??
          null;

        if (!derivedProjectId) {
          setTasks([]);
          return;
        }

        const data = await fetchTasksByStoryId(storyId, derivedProjectId, derivedSubProjectId);
        if (requestId !== activeFetchRequestRef.current) return;
        const arr = Array.isArray(data) ? data : [];
        setTasks(
          arr.map((t) => ({
            ...t,
            __storyId: Number(storyId),
            __projectId: Number(derivedProjectId),
            __subProjectId: derivedSubProjectId ? Number(derivedSubProjectId) : null
          }))
        );
        return;
      }

      // Global mode: All Projects + All Subprojects + All User Stories.
      // Prioritize this branch before story-group aggregation so dashboard counts and
      // PM tasks table use the same source in the unscoped view.
      if (isGlobalAllScope) {
        const data = await fetchAllTasks();
        if (requestId !== activeFetchRequestRef.current) return;
        setTasks(Array.isArray(data) ? data : []);
        return;
      }

      // Aggregated mode: "All User Stories"
      if (allStoriesSelected && Array.isArray(storiesForAll) && storiesForAll.length > 0) {
        // Reduce request count:
        // Old approach: fetch tasks once per story (N API calls).
        // New approach: group stories by project/subproject and fetch tasks once per group,
        // then filter by story ids (typically far fewer API calls).
        const groupsByScope = new Map(); // key -> { pid, spid, storyIds:Set<string> }

        for (const st of storiesForAll) {
          const sid = st.id ?? st.User_Story_Id ?? st.user_story_id ?? st.UserStoryId;
          const pid = st.projectId ?? st.Project_Id ?? st.project_id ?? st.ProjectId ?? null;
          const rawSpid = st.subProjectId ?? st.Sub_Project_Id ?? st.sub_project_id ?? st.SubProjectId ?? null;
          const spid = rawSpid === "" || rawSpid === undefined ? null : rawSpid;

          if (!sid || !pid) continue;

          const key = `${pid}|${spid ?? ""}`;
          if (!groupsByScope.has(key)) {
            groupsByScope.set(key, { pid, spid, storyIds: new Set() });
          }
          groupsByScope.get(key).storyIds.add(String(sid));
        }

        const groups = Array.from(groupsByScope.values());

        const tasksPerGroup = await Promise.all(
          groups.map(async ({ pid, spid, storyIds }) => {
            const list = await fetchTasksByProject(pid, spid).catch(() => []);
            const arr = Array.isArray(list) ? list : [];

            return arr
              .filter((t) => {
                const taskStoryId =
                  t?.Story_Id ?? t?.story_id ?? t?.StoryId ?? t?.storyId ?? t?.User_Story_Id ?? t?.user_story_id;
                if (taskStoryId == null) return false;
                return storyIds.has(String(taskStoryId));
              })
              .map((t) => {
                const taskStoryId =
                  t?.Story_Id ?? t?.story_id ?? t?.StoryId ?? t?.storyId ?? t?.User_Story_Id ?? t?.user_story_id;

                return {
                  ...t,
                  __storyId: taskStoryId != null ? Number(taskStoryId) : null,
                  __projectId: Number(pid),
                  __subProjectId: spid != null ? Number(spid) : null,
                };
              });
          })
        );

        if (requestId !== activeFetchRequestRef.current) return;
        setTasks(tasksPerGroup.flat());
        return;
      }

      if (requestId !== activeFetchRequestRef.current) return;
      setTasks([]);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      if (requestId !== activeFetchRequestRef.current) return;
      setTasks([]);
      lastFetchSignatureRef.current = "";
    } finally {
      if (requestId !== activeFetchRequestRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dataActive) {
      return;
    }
    loadTasks();
  }, [dataActive, storyId, projectId, subProjectId, storiesForAllIdsKey, allStoriesSelected, loadingUserStoriesForTasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const title = (t.Title || t.title || "").toLowerCase();
      const search = (titleSearch || "").trim().toLowerCase();
      if (search && !title.includes(search)) return false;
      if (statusFilter) {
        const rowStatus = String(t.Status || t.status || "").trim();
        const selectedStatus = String(statusFilter || "").trim();
        const rowBucket = taskStatusBucket(rowStatus);
        const selectedBucket = taskStatusBucket(selectedStatus);
        const exactMatch = rowStatus.toLowerCase() === selectedStatus.toLowerCase();
        const bucketMatch = selectedBucket && rowBucket === selectedBucket;
        if (!exactMatch && !bucketMatch) return false;
      }
      if (priorityFilter && (t.Priority || t.priority || "").toLowerCase() !== priorityFilter.toLowerCase()) return false;
      return true;
    });
  }, [tasks, statusFilter, priorityFilter, titleSearch]);

  const freeSearchFilteredTasks = useMemo(() => {
    const q = String(freeSearch || "").trim().toLowerCase();
    if (!q) return filteredTasks;
    return (filteredTasks || []).filter((t) => {
      const bag = [
        t?.Title ?? t?.title ?? "",
        t?.Description ?? t?.description ?? "",
        t?.Priority ?? t?.priority ?? "",
        t?.Status ?? t?.status ?? "",
        t?.Start_Date ?? t?.start_date ?? "",
        t?.End_Date ?? t?.end_date ?? "",
        t?.Created_By_Name ?? t?.created_by_name ?? "",
        t?.Created_On ?? t?.created_on ?? ""
      ].map((v) => String(v || "").toLowerCase());
      return bag.some((v) => v.includes(q));
    });
  }, [filteredTasks, freeSearch]);

  const getTaskSortValue = (task, columnId) => {
    const rawDate = (value) => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    switch (columnId) {
      case "title":
        return String(task?.Title ?? task?.title ?? "").toLowerCase();
      case "description":
        return String(task?.Description ?? task?.description ?? "").toLowerCase();
      case "priority":
        return String(task?.Priority ?? task?.priority ?? "").toLowerCase();
      case "status":
        return String(task?.Status ?? task?.status ?? "").toLowerCase();
      case "startDate":
        return rawDate(task?.Start_Date ?? task?.start_date);
      case "endDate":
        return rawDate(task?.End_Date ?? task?.end_date);
      case "createdBy":
        return String(task?.Created_By_Name ?? task?.created_by_name ?? "").toLowerCase();
      case "createdOn":
        return rawDate(task?.Created_On ?? task?.created_on);
      default:
        return "";
    }
  };

  const sortedTasks = useMemo(() => {
    if (!sortColumn) return freeSearchFilteredTasks;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFilteredTasks].sort((a, b) => {
      const va = getTaskSortValue(a, sortColumn);
      const vb = getTaskSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Task_Id ?? a?.task_id ?? 0);
      const idb = Number(b?.Task_Id ?? b?.task_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFilteredTasks, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / rowsPerPage));
  const startIdx = (taskPage - 1) * rowsPerPage;
  const currentTasks = sortedTasks.slice(startIdx, startIdx + rowsPerPage);

  useEffect(() => {
    if (taskPage > totalPages && totalPages > 0) {
      setTaskPage(1);
    }
  }, [taskPage, totalPages]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setTaskPage(1);
    localStorage.setItem(TASKS_ROWS_PER_PAGE_KEY, String(next));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    setDeletingId(taskId);
    try {
      await deleteTask(taskId);
      setTasks((prev) => prev.filter((t) => (t.Task_Id ?? t.task_id) !== taskId));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error deleting task:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete task.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddTaskClick = () => {
    // In "All User Stories" mode we create tasks under the first available story.
    const firstStory = (storiesForAll || [])[0];
    const addStoryId = storyId
      ? storyId
      : firstStory?.id ?? firstStory?.User_Story_Id ?? firstStory?.user_story_id ?? null;
    const addProjectId =
      projectId ??
      (firstStory?.projectId ?? firstStory?.Project_Id ?? firstStory?.project_id ?? firstStory?.ProjectId ?? null);
    const addSubProjectId =
      subProjectId ??
      (firstStory?.subProjectId ?? firstStory?.Sub_Project_Id ?? firstStory?.sub_project_id ?? firstStory?.SubProjectId ?? null);

    if (!addStoryId || !addProjectId) {
      alert("Select a user story to add a task.");
      return;
    }

    const addPath = addSubProjectId
      ? `/projects/${addProjectId}/subprojects/${addSubProjectId}/user-stories/${addStoryId}/tasks/add`
      : `/projects/${addProjectId}/user-stories/${addStoryId}/tasks/add`;
    navigate(addPath);
  };

  const handleSort = (columnId) => {
    setTaskPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const resolveTaskContext = (task) => {
    const ctxStoryId =
      task?.__storyId ??
      task?.Story_Id ??
      task?.story_id ??
      storyId ??
      null;

    const ctxProjectId =
      task?.__projectId ??
      task?.Project_Id ??
      task?.project_id ??
      projectId ??
      null;

    const ctxSubProjectId =
      task?.__subProjectId ??
      task?.Sub_Project_Id ??
      task?.sub_project_id ??
      subProjectId ??
      null;

    return {
      storyId: ctxStoryId ? Number(ctxStoryId) : null,
      projectId: ctxProjectId ? Number(ctxProjectId) : null,
      subProjectId: ctxSubProjectId ? Number(ctxSubProjectId) : null
    };
  };

  return (
    <div className="userstories-table-panel">
      {cardTitle ? (
        <h3 className="viewlead-section-title" style={{ marginTop: 0, marginBottom: 16 }}>
          {cardTitle}
        </h3>
      ) : null}
      <div className="projects-header-row" style={{ marginBottom: 12 }}>
        {(projects.length > 0 || storyId) && (
        <div className="userstories-filters-row projects-filters-row tasks-filters-row" style={{ flex: 1, marginBottom: 0 }}>
          <input
            type="text"
            className="userstories-title-search"
            placeholder="Search by title..."
            value={titleSearch}
            onChange={(e) => setTitleSearch(e.target.value)}
          />

          {projects.length > 0 && (
          <>
          <div className="domain-filter-dropdown" ref={projectDropdownRef} style={{ minWidth: 180 }}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              style={{
                height: 38,
                borderRadius: 6,
                border: "1px solid #D6E0EA",
                padding: "0 12px",
                fontSize: 14,
                background: "#fff",
                minWidth: 180,
                opacity: 1,
                cursor: "pointer",
              }}
              onClick={() => {
                setProjectDropdownOpen((o) => !o);
                setSubprojectDropdownOpen(false);
                setStoryDropdownOpen(false);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setProjectDropdownOpen((o) => !o);
                  setSubprojectDropdownOpen(false);
                  setStoryDropdownOpen(false);
                }
              }}
            >
              <span>
                {selectedProjectIdValue
                  ? projects.find((p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectIdValue))?.Project_Name ??
                    projects.find((p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectIdValue))?.project_name ??
                    projects.find((p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectIdValue))?.name ??
                    "All Projects"
                  : "All Projects"}
              </span>
              <FaChevronDown className={`domain-filter-chevron ${projectDropdownOpen ? "open" : ""}`} />
            </div>

            {projectDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search projects..."
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!selectedProjectIdValue ? "selected" : ""}`}
                    onClick={() => {
                      onProjectChange?.("");
                      setProjectDropdownOpen(false);
                      setProjectSearchQuery("");
                    }}
                  >
                    All Projects
                  </div>
                  {(projects || [])
                    .filter(
                      (p) =>
                        !projectSearchQuery.trim() ||
                        String(p.Project_Name ?? p.project_name ?? p.name ?? "")
                          .toLowerCase()
                          .includes(projectSearchQuery.trim().toLowerCase())
                    )
                    .map((p) => {
                      const id = p.Project_Id ?? p.project_id ?? p.id;
                      const name = p.Project_Name ?? p.project_name ?? p.name ?? "";
                      return (
                        <div
                          key={id}
                          className={`domain-filter-option ${String(selectedProjectIdValue) === String(id) ? "selected" : ""}`}
                          onClick={() => {
                            onProjectChange?.(String(id));
                            setProjectDropdownOpen(false);
                            setProjectSearchQuery("");
                          }}
                        >
                          {name}
                        </div>
                      );
                    })}
                  {(projects || [])
                    .filter(
                      (p) =>
                        !projectSearchQuery.trim() ||
                        String(p.Project_Name ?? p.project_name ?? p.name ?? "")
                          .toLowerCase()
                          .includes(projectSearchQuery.trim().toLowerCase())
                    ).length === 0 && <div className="domain-filter-no-results">No projects found</div>}
                </div>
              </div>
            )}
          </div>

          <div className="domain-filter-dropdown" ref={subprojectDropdownRef} style={{ minWidth: 180 }}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              style={{
                height: 38,
                borderRadius: 6,
                border: "1px solid #D6E0EA",
                padding: "0 12px",
                fontSize: 14,
                background: "#fff",
                minWidth: 180,
                opacity: loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue ? 0.6 : 1,
                cursor: loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue ? "default" : "pointer",
              }}
              onClick={() => {
                if (loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue) return;
                setSubprojectDropdownOpen((o) => !o);
                setProjectDropdownOpen(false);
                setStoryDropdownOpen(false);
              }}
              role="button"
              tabIndex={loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue ? -1 : 0}
              onKeyDown={(e) => {
                if (loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSubprojectDropdownOpen((o) => !o);
                  setProjectDropdownOpen(false);
                  setStoryDropdownOpen(false);
                }
              }}
            >
              <span>
                {selectedSubprojectIdValue
                  ? subprojects.find((sp) => String(sp.id) === String(selectedSubprojectIdValue))?.name ?? "All Subprojects"
                  : "All Subprojects"}
              </span>
              <FaChevronDown className={`domain-filter-chevron ${subprojectDropdownOpen ? "open" : ""}`} />
            </div>

            {subprojectDropdownOpen && !(loadingSubprojects || subprojects.length === 0 || !selectedProjectIdValue) && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search subprojects..."
                  value={subprojectSearchQuery}
                  onChange={(e) => setSubprojectSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!selectedSubprojectIdValue ? "selected" : ""}`}
                    onClick={() => {
                      onSubprojectChange?.("");
                      setSubprojectDropdownOpen(false);
                      setSubprojectSearchQuery("");
                    }}
                  >
                    All Subprojects
                  </div>
                  {(subprojects || [])
                    .filter(
                      (sp) =>
                        !subprojectSearchQuery.trim() ||
                        String(sp.name ?? "")
                          .toLowerCase()
                          .includes(subprojectSearchQuery.trim().toLowerCase())
                    )
                    .map((sp) => (
                      <div
                        key={sp.id}
                        className={`domain-filter-option ${String(selectedSubprojectIdValue) === String(sp.id) ? "selected" : ""}`}
                        onClick={() => {
                          onSubprojectChange?.(String(sp.id));
                          setSubprojectDropdownOpen(false);
                          setSubprojectSearchQuery("");
                        }}
                      >
                        {sp.name}
                      </div>
                    ))}
                  {(subprojects || [])
                    .filter(
                      (sp) =>
                        !subprojectSearchQuery.trim() ||
                        String(sp.name ?? "")
                          .toLowerCase()
                          .includes(subprojectSearchQuery.trim().toLowerCase())
                    ).length === 0 && <div className="domain-filter-no-results">No subprojects found</div>}
                </div>
              </div>
            )}
          </div>

          <div className="domain-filter-dropdown" ref={storyDropdownRef} style={{ minWidth: 210 }}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              style={{
                height: 38,
                borderRadius: 6,
                border: "1px solid #D6E0EA",
                padding: "0 12px",
                fontSize: 14,
                background: "#fff",
                minWidth: 210,
                opacity: loadingUserStoriesForTasks ? 0.6 : 1,
                cursor: loadingUserStoriesForTasks ? "default" : "pointer",
              }}
              onClick={() => {
                if (loadingUserStoriesForTasks) return;
                setStoryDropdownOpen((o) => !o);
                setProjectDropdownOpen(false);
                setSubprojectDropdownOpen(false);
              }}
              role="button"
              tabIndex={loadingUserStoriesForTasks ? -1 : 0}
              onKeyDown={(e) => {
                if (loadingUserStoriesForTasks) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStoryDropdownOpen((o) => !o);
                  setProjectDropdownOpen(false);
                  setSubprojectDropdownOpen(false);
                }
              }}
            >
              <span>
                {selectedStoryIdValue
                  ? (storiesForAll || []).find((st) => String(st.id) === String(selectedStoryIdValue))?.title ?? "All User Stories"
                  : "All User Stories"}
              </span>
              <FaChevronDown className={`domain-filter-chevron ${storyDropdownOpen ? "open" : ""}`} />
            </div>

            {storyDropdownOpen && !loadingUserStoriesForTasks && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search user stories..."
                  value={storySearchQuery}
                  onChange={(e) => setStorySearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!selectedStoryIdValue ? "selected" : ""}`}
                    onClick={() => {
                      onStoryChange?.("");
                      setStoryDropdownOpen(false);
                      setStorySearchQuery("");
                    }}
                  >
                    All User Stories
                  </div>
                  {(storiesForAll || [])
                    .filter(
                      (st) =>
                        !storySearchQuery.trim() ||
                        String(st.title ?? "")
                          .toLowerCase()
                          .includes(storySearchQuery.trim().toLowerCase())
                    )
                    .map((st) => (
                      <div
                        key={st.id}
                        className={`domain-filter-option ${String(selectedStoryIdValue) === String(st.id) ? "selected" : ""}`}
                        onClick={() => {
                          onStoryChange?.(String(st.id));
                          setStoryDropdownOpen(false);
                          setStorySearchQuery("");
                        }}
                      >
                        {st.title}
                      </div>
                    ))}
                  {(storiesForAll || [])
                    .filter(
                      (st) =>
                        !storySearchQuery.trim() ||
                        String(st.title ?? "")
                          .toLowerCase()
                          .includes(storySearchQuery.trim().toLowerCase())
                    ).length === 0 && <div className="domain-filter-no-results">No stories found</div>}
                </div>
              </div>
            )}
          </div>

          </>
          )}

          <select
            className="userstories-priority-filter userstories-status-filter"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">All Priority</option>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            className="userstories-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            {taskStatuses.map((s) => (
              <option key={s.Status_Id ?? s.status_id} value={s.Status ?? s.status}>
                {s.Status ?? s.status}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              setDashboardPresetActive(false);
              setTitleSearch("");
              setStatusFilter("");
              setPriorityFilter("");
              setTaskPage(1);
              onProjectChange?.("");
              onSubprojectChange?.("");
              onStoryChange?.("");
              setProjectDropdownOpen(false);
              setSubprojectDropdownOpen(false);
              setStoryDropdownOpen(false);
              setProjectSearchQuery("");
              setSubprojectSearchQuery("");
              setStorySearchQuery("");
            }}
            style={{ marginLeft: 8 }}
          >
            Clear
          </button>
        </div>
        )}
        <div className="header-actions">
          <button
            type="button"
            className="btn-add-lead"
            onClick={handleAddTaskClick}
            disabled={loading || (!storyId && allStoriesSelected && (!storiesForAll || storiesForAll.length === 0))}
          >
            Add Task
          </button>
        </div>
      </div>

      <div className="search-row-2">
        <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={freeSearch}
            onChange={(e) => {
              setFreeSearch(e.target.value);
              setTaskPage(1);
            }}
            title="Search across task table columns"
          />
        </div>
      </div>

      {loading ? (
        <div className="project-subprojects-loading">Loading tasks...</div>
      ) : (
        <>
          <div className="projects-container tasks-table-container userstories-table-container">
            <div className="table-wrapper userstories-table-wrapper">
              <div className="table-container">
                <table className="data-table projects-table-frozen">
                  <thead>
                    <tr>
                      <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}>
                        <div className="projects-th-sort-row">
                          <SupportSortableHeader columnId="title" label="Title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={0} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(1)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="description" label="Description" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={1} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(2)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="priority" label="Priority" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={2} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(3)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={3} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(4)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="startDate" label="Start Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={4} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(5)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="endDate" label="End Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={5} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(6)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdBy" label="Created By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={6} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(7)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdOn" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={7} />
                      </th>
                      <th className="col-sticky col-action" {...getColProps(8)}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTasks.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="userstories-empty-cell">
                          No tasks yet.
                        </td>
                      </tr>
                    ) : (
                      currentTasks.map((task) => {
                        const id = task.Task_Id || task.task_id;
                        return (
                          <tr key={id}>
                            <td className="col-sticky col-0">
                              <span
                                className="table-name-link"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  const { storyId: ctxStoryId, projectId: ctxProjectId, subProjectId: ctxSubProjectId } =
                                    resolveTaskContext(task);
                                  if (!ctxStoryId || !ctxProjectId) return;
                                  const viewPath = ctxSubProjectId
                                    ? `/projects/${ctxProjectId}/subprojects/${ctxSubProjectId}/user-stories/${ctxStoryId}/tasks/${id}/view`
                                    : `/projects/${ctxProjectId}/user-stories/${ctxStoryId}/tasks/${id}/view`;
                                  navigate(viewPath);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    const { storyId: ctxStoryId, projectId: ctxProjectId, subProjectId: ctxSubProjectId } =
                                      resolveTaskContext(task);
                                    if (!ctxStoryId || !ctxProjectId) return;
                                    const viewPath = ctxSubProjectId
                                      ? `/projects/${ctxProjectId}/subprojects/${ctxSubProjectId}/user-stories/${ctxStoryId}/tasks/${id}/view`
                                      : `/projects/${ctxProjectId}/user-stories/${ctxStoryId}/tasks/${id}/view`;
                                    navigate(viewPath);
                                  }
                                }}
                              >
                                {task.Title || task.title || "—"}
                              </span>
                            </td>
                            <td style={{ textAlign: "left", maxWidth: 300 }}>
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 300
                                }}
                                title={task.Description || task.description || ""}
                              >
                                {task.Description || task.description || "—"}
                              </span>
                            </td>
                            <td>
                              {(() => {
                                const priority =
                                  task.Priority ?? task.priority ?? "";
                                const priorityClass = priority
                                  ? `status-${String(priority)
                                      .toLowerCase()
                                      .replace(/\s/g, "-")}`
                                  : "status-not-set";
                                return (
                                  <span className={`status-badge ${priorityClass}`}>
                                    {priority || "Not Set"}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <span className="task-status-text">
                                {task.Status || task.status || "—"}
                              </span>
                            </td>
                            <td>{formatDate(task.Start_Date || task.start_date)}</td>
                            <td>{formatDate(task.End_Date || task.end_date)}</td>
                            <td>{task.Created_By_Name || task.created_by_name || "—"}</td>
                            <td>{formatDate(task.Created_On || task.created_on)}</td>
                            <td className="col-sticky col-action">
                              <div className="project-actions-cell">
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                  title="Edit task"
                                  onClick={() => {
                                    const { storyId: ctxStoryId, projectId: ctxProjectId, subProjectId: ctxSubProjectId } =
                                      resolveTaskContext(task);
                                    if (!ctxStoryId || !ctxProjectId) return;
                                    const editPath = ctxSubProjectId
                                      ? `/projects/${ctxProjectId}/subprojects/${ctxSubProjectId}/user-stories/${ctxStoryId}/tasks/${id}/edit`
                                      : `/projects/${ctxProjectId}/user-stories/${ctxStoryId}/tasks/${id}/edit`;
                                    navigate(editPath);
                                  }}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                  title="Delete task"
                                  disabled={deletingId === id}
                                  onClick={() => handleDeleteTask(id)}
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {!loading && sortedTasks.length > 0 ? (
              <TablePagination
                currentPage={taskPage}
                totalItems={sortedTasks.length}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                onPageChange={setTaskPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                itemLabel="tasks"
                rowsPerPageSelectId="tasks-rows-per-page"
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default TasksTable;

